import React from 'react'
import {
  Heading,
  Body,
  PriceTag,
  TextButton,
  Link,
  SelectInput,
  getImgProps,
  Price as PriceComponent,
  Img,
} from '@superhi/design'
import { useMutation } from '@apollo/client'

import { Wrapper, ImageWrapper, Content, Options, Price } from './styles'
import { LINE_ITEM_QUANTITY_EDIT, LINE_ITEM_REMOVE } from './mutations'

import events from 'src/events'
import { API } from 'src/api'
import { buildPath } from 'src/routing'

// const getVariantName = (productName: string, variantName?: string) =>
//   `${productName}${variantName ? ` (${variantName})` : ''}`;

const getProductPath = (
  slug: string,
  typename: API.BasketBasket_basket_lineItems_product['__typename'],
) =>
  buildPath(
    typename === 'ProductCourse' ? 'courses-view' : 'book',
    typename === 'ProductCourse' ? { params: { slug } } : undefined,
  )

type Props = {
  id: string
  title: string
  slug: string
  type: API.BasketBasket_basket_lineItems_product['__typename']
  imageUrl: string
  variant: {
    id: string
  }
  discount: {
    value: number
    currencyCode: API.CurrencyCode
  }
  price: {
    value: number
    currencyCode: API.CurrencyCode
  }
  quantity: number
  maxQuantity?: number
}

const LineItem: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  type,
  slug,
  title,
  imageUrl,
  variant,
  discount,
  price,
  quantity,
  maxQuantity = 1,
}) => {
  const [editQuantity, editQuantityResponse] = useMutation<
    API.CartLineItemQuantityEdit,
    API.CartLineItemQuantityEditVariables
  >(LINE_ITEM_QUANTITY_EDIT)

  const [remove, removeResponse] = useMutation<
    API.CartLineItemRemove,
    API.CartLineItemRemoveVariables
  >(LINE_ITEM_REMOVE, {
    onCompleted: () => {
      // trigger({ name: 'Product Removed', config: { variantId: variant.id, quantity } });
    },
  })

  const onRemove = React.useCallback(
    async (lineItemId: string) => {
      await remove({
        variables: { lineItemId },
        refetchQueries: ['CartBasket'],
      })
    },
    [remove],
  )

  const onChangeQuantity = React.useCallback(
    async (lineItemId: string, newQuantity: number) => {
      try {
        const isRemoval = newQuantity < quantity

        await editQuantity({
          variables: { lineItemId, quantity: newQuantity },
        })

        if (isRemoval) {
          // trigger({
          //   name: 'Product Removed',
          //   config: { variantId: variant.id, quantity: quantity - newQuantity },
          // });
        } else {
          events.productAdded({
            variantId: variant.id,
            quantity: newQuantity - quantity,
          })
        }
      } catch (e) {
        console.log(e)
      }
    },
    [quantity, variant.id, editQuantity],
  )

  const QUANTITY_OPTIONS = React.useMemo(
    () =>
      new Array(maxQuantity)
        .fill(null)
        .map((_, i) => ({ display: (i + 1).toString(), value: i + 1 })),
    [maxQuantity],
  )

  const imageProps = React.useMemo(
    () =>
      getImgProps(imageUrl, {
        small: 20,
        medium: 20,
        large: 10,
        xlarge: 10,
      }),
    [imageUrl],
  )

  const fullPrice = price.value * quantity
  const discountedPrice = fullPrice - discount.value
  const isDiscounted = discount.value > 0
  const href = React.useMemo(() => getProductPath(slug, type), [slug, type])

  return (
    <Wrapper $isLoading={removeResponse.loading || editQuantityResponse.loading}>
      <ImageWrapper>
        <Img {...imageProps} isRounded aspectRatio="16:9" fit="cover" alt="" />
      </ImageWrapper>

      <Content>
        <Heading level="4">
          <Link href={href} color="GREY_90" hoverColor="BLUE_50">
            {title}
          </Link>
        </Heading>

        <Options>
          {maxQuantity > 1 && (
            <SelectInput<number>
              id={`input_${id}_quantity`}
              ariaLabel="Quantity"
              name="quantity"
              value={quantity}
              onChange={(newQuantity) => onChangeQuantity(id, newQuantity)}
              options={QUANTITY_OPTIONS}
              disabled={removeResponse.loading}
            />
          )}

          <TextButton onClick={() => onRemove(id)} hoverColor="BLUE_50">
            <Body level="3">Remove</Body>
          </TextButton>
        </Options>
      </Content>

      <Price>
        <PriceTag
          level="2"
          fullPrice={
            isDiscounted ? PriceComponent.format(fullPrice, price.currencyCode) : undefined
          }
        >
          {discountedPrice <= 0
            ? 'FREE'
            : PriceComponent.format(discountedPrice, price.currencyCode)}
        </PriceTag>
      </Price>
    </Wrapper>
  )
}

export default LineItem
